import { HStack, PageContainer, PageHeader, Spread, VStack } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { AppliedFilter, AppliedFiltersBar } from '@/components/DataTable/AppliedFiltersBar'
import { Filter, FilterControls } from '@/components/DataTable/FilterControls'
import { ExportCsvMenu, ExportCsvMenuItem } from '@/components/ExportCsv/ExportCsvMenu'
import { useCrtCurrencySync } from '@/hooks/crt/useCrtCurrencySync'
import { useCrtVersionSelectionSync } from '@/hooks/crt/useCrtVersionSelectionSync'
import useBrandFilter from '@/hooks/filters/useBrandFilter'
import useMaterialFilter from '@/hooks/filters/useMaterialFilter'
import useProductCategoryFilter from '@/hooks/filters/useProductCategoryFilter'
import usePermissions from '@/hooks/permissions/usePermissions'
import { useCrtVersions } from '@/hooks/queries/crt/useCrtVersions'
import {
  arReportQueryOptions,
  pnlTableSearchSchema,
  useActualsPnl,
  useForecastsPnl,
} from '@/hooks/queries/crt/usePnl'
import { useVolumeScenariosList } from '@/hooks/queries/crt/useVolumeScenarios'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { getClearedFilters, resetFiltersPanel, useAppliedFilters } from '@/lib/utils/filters'
import { MAX_PAGE_SIZE } from '@repo/constants'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { parseISO } from 'date-fns'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  buildKpiDashboardCsv,
  resolveKpiRows,
  toKpiDashboardCsvExport,
  volumesOf,
} from '../-components/kpi-dashboard-csv'
import { PerMilSwitch } from '../-components/PerMilSwitch'
import { getPnlView, type PnlView, toPnlViewSearch } from '../-components/pnl-view'
import { PnlViewSelector } from '../-components/PnlViewSelector'
import { CrtVersionsFooter } from '../-components/CrtVersionsFooter'
import { AR_TABLE_SECTIONS } from './-components/ar-table-config'
import { ArDashboardTable } from './-components/ArDashboardTable'
import { resolveCashBreakdownSide } from './-components/cash-breakdown'
import { CashSummaryCard } from './-components/CashSummaryCard'
import { CrtVersionComputingNote } from '../-components/CrtVersionComputingNote'

// Configuration is owned by the P&L Summary and inherited through the URL, so these badges are
// read-only here: clicking one has nothing to open.
const CONFIG_BADGE_KEYS = ['volumeScenario']

/**
 * The Cash dashboard: the same summary data as the P&L Summary, cut down to the lines
 * that describe what customers owe (see `-components/ar-table-config.ts` for the ladder and its
 * PTT/VAT scope caveat). It reads the same two endpoints with the same query keys, so the two pages
 * can never disagree on a shared figure.
 *
 * Deliberately narrower than the P&L Summary: only three material filters (product category, brand,
 * and material for UAT), only the AR Report CSV export, and no configuration sheet or missing-import
 * warnings — this
 * is a cut of the P&L Summary, stepped into from its footer link, so it inherits the volume scenario
 * and the assumption sandbox through the search params rather than re-offering them. The view (actuals +
 * forecast, actuals only, next year) and per-mil stay editable above the table because they are read at a
 * glance. The period, currency and CRT versions come from the header like everywhere else, and a version
 * created here is the same period-scoped object the P&L Summary creates.
 */
export const Route = createFileRoute('/_auth/_modules/crt/kpi-reports/cash-dashboard/')({
  component: RouteComponent,
  validateSearch: (search) => pnlTableSearchSchema.parse(search),
})

function RouteComponent() {
  const { t } = useTranslation(['crt', 'common'])
  const { formatMonth } = useLocalizedDate()
  const queryClient = useQueryClient()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { crtPeriod, loadVersion, markSaved } = useCrtVersionSelectionSync({
    search,
    navigate: (search) => navigate({ search }),
  })

  // The same three views as the P&L Summary, so a view picked there carries over here.
  const view = getPnlView(search)
  // Next year has no actuals yet: fetch forecast-only (`year=NY`) and skip the actuals query.
  const isNextYear = view === 'nextYear'
  // Actuals-only: every month actualised in the selected period's year, with no forecast.
  const isActualsOnly = view === 'actualsOnly'

  const { productCategoryFilter, productCategories, setProductCategories } =
    useProductCategoryFilter(search.productCategory)
  const { brandFilter, brands, setBrands } = useBrandFilter(search.brand)
  // Exposed for UAT purposes only — it lets a tester tie a figure back to a single SKU.
  const { materialFilter, material, setMaterial } = useMaterialFilter(search.material)

  const filters = [productCategoryFilter, brandFilter, materialFilter]

  const selectedFilters = {
    productCategory: productCategories?.length ? productCategories : undefined,
    brand: brands?.length ? brands : undefined,
    material: material || undefined,
  }

  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false)

  // Currency is display-only and picked in the header: it never reaches the backend, so it stays out of
  // `appliedFilters`. Called after the version sync so both can mirror into the URL on arrival.
  const { conversion } = useCrtCurrencySync({
    search,
    navigate: (update) => navigate({ search: update, replace: true }),
  })

  // Resolve the applied scenario's name for its badge — only fetch when a scenario is pinned and the user
  // can view scenarios (a shared URL opened without the permission still recomputes server-side).
  const { canUser } = usePermissions()
  const canViewScenarios = canUser('crt_volume_scenarios_view')
  const { data: scenariosData } = useVolumeScenariosList(
    { pageSize: MAX_PAGE_SIZE },
    { enabled: canViewScenarios && !isActualsOnly && search.volumeScenarioId != null },
  )
  // The inherited sandbox stays in the URL through actuals-only so switching back restores it, but it
  // applies to neither the request nor the badges there.
  const sandbox = isActualsOnly
    ? {}
    : {
        pricingAssumptionIds: search.pricingAssumptionIds,
        taxAssumptionIds: search.taxAssumptionIds,
        manualInvestmentIds: search.manualInvestmentIds,
        // Forward the loaded version so the backend can serve its precomputed rows; the handler drops back to
        // a live compute when the selection has diverged from the version. A brand or material filter is
        // ineligible for that fast path either way — it has no stored grain.
        loadedVersionId: search.loadedVersionId,
        volumeScenarioId: search.volumeScenarioId,
      }
  const selectedScenario = scenariosData?.results.find((s) => s.id === sandbox.volumeScenarioId)

  // The controls that stay editable here apply straight away — there is no panel to stage them in.
  const handleViewChange = (view: PnlView) => {
    navigate({ search: { ...search, ...toPnlViewSearch(view) } })
  }

  const handlePerMilChange = (isPerMil: boolean) => {
    navigate({ search: { ...search, isPerMil: isPerMil || undefined } })
  }

  // Badges for the filters panel, plus the inherited configuration shown read-only
  const appliedFilterBadges: AppliedFilter[] = [
    ...useAppliedFilters(filters as Filter<unknown>[], search),
    ...(selectedScenario
      ? [
          {
            key: 'volumeScenario',
            name: t('kpiReports.viewConfigurations.volumeScenario'),
            value: selectedScenario.name,
          },
        ]
      : []),
  ]

  const handleBadgeClick = (filter: AppliedFilter) => {
    if (filter.key && CONFIG_BADGE_KEYS.includes(filter.key)) return

    setIsFiltersPanelOpen(true)
  }

  // Re-sync the panel's local state to the applied URL params whenever it opens, so an aborted edit
  // (open → change → close without applying) does not linger on the next open.
  const handleFiltersPanelOpenChange = () => {
    setIsFiltersPanelOpen((open) => {
      if (!open) resetFiltersPanel(filters as Filter<unknown>[], search)
      return !open
    })
  }

  const showResults = () => {
    navigate({ search: { ...search, ...selectedFilters } })
  }

  const resetAllFilters = () => {
    setProductCategories([])
    setBrands([])
    setMaterial('')
    navigate({ search: { ...search, ...getClearedFilters(selectedFilters) } })
  }

  const appliedFilters = {
    productCategory: search.productCategory,
    brand: search.brand,
    material: search.material,
    isPerMil: search.isPerMil,
    year: search.year,
    // Without the sandbox, actuals-only reads its precomputed full-year slice rather than a live compute.
    actualsOnly: isActualsOnly || undefined,
    ...sandbox,
  }

  const {
    data: actualsData,
    isPending: isActualsPending,
    error: actualsError,
  } = useActualsPnl(crtPeriod, appliedFilters, { enabled: !isNextYear })
  const {
    data: forecastsData,
    isPending: isForecastsPending,
    error: forecastsError,
  } = useForecastsPnl(crtPeriod, appliedFilters, { enabled: !isActualsOnly })

  // Keep the skeletons until BOTH queries resolve — otherwise the faster one clears them while the
  // other's cells are still empty. In next-year mode the actuals query is disabled (stays `pending`
  // forever), and in actuals-only mode the forecasts query is, so ignore the disabled one.
  const isPending = (!isActualsOnly && isForecastsPending) || (!isNextYear && isActualsPending)
  const error = isNextYear ? forecastsError : actualsError
  const months = (isNextYear ? forecastsData : actualsData)?.meta.months ?? []

  // Shared (by query key) with the header selector and the version sync: resolves the loaded version's
  // pins, so the breakdown can tell whether the page still shows that version unchanged.
  const { data: periodVersions } = useCrtVersions(crtPeriod)
  const breakdownAvailability = resolveCashBreakdownSide({
    crtPeriod,
    view,
    search,
    periodVersions,
  })

  // The AR Report is built server-side for exactly what the page shows. Per-mil only changes how the
  // table displays its figures, not the report's totals, so it stays out of the request and its cache key.
  const { isPerMil: _isPerMil, ...arReportFilters } = appliedFilters
  // Always refetched: a cached CSV would outlive a recompute the page's own queries have since picked up.
  // A version computed before the report's lines were stored is exported from current inputs instead,
  // which may not match the figures it shows, so the download says so.
  const exportArReport = async () => {
    const { warningCode, ...csvExport } = await queryClient.fetchQuery(
      arReportQueryOptions(crtPeriod, arReportFilters),
    )
    return warningCode === 'storedLinesMissing'
      ? {
          ...csvExport,
          warning: {
            title: t('kpiReports.cashDashboard.export.storedLinesMissing.title'),
            description: t('kpiReports.cashDashboard.export.storedLinesMissing.description'),
          },
        }
      : csvExport
  }
  // The report reads both sides whatever the view, so the endpoint requires both permissions.
  const canExportArReport = canUser('crt_pnl_actuals_view') && canUser('crt_pnl_forecasts_view')

  // The table as displayed, built from the same loaded trees the table reads (same as the P&L Summary's
  // dashboard export), with the VAT breakdown expanded into a `breakdown` column.
  const dashboardRows = resolveKpiRows(
    AR_TABLE_SECTIONS,
    isNextYear ? undefined : actualsData?.breakdown,
    isActualsOnly ? undefined : forecastsData?.breakdown,
  )
  const exportDashboard = () =>
    Promise.resolve(
      toKpiDashboardCsvExport(
        buildKpiDashboardCsv({
          rows: dashboardRows,
          monthLabels: months.map((month) => formatMonth(parseISO(month), { month: 'short' })),
          labelOf: (key) => t(`kpis.labels.${key}`),
          headers: {
            description: t('kpiReports.columns.description'),
            breakdown: t('kpiReports.columns.breakdown'),
            fullYearTotal: t('kpiReports.columns.fullYearTotal'),
          },
          currency: conversion,
          perMilVolumes: search.isPerMil
            ? volumesOf(
                isNextYear ? undefined : actualsData?.breakdown,
                isActualsOnly ? undefined : forecastsData?.breakdown,
              )
            : undefined,
        }),
        'cash-dashboard',
      ),
    )

  return (
    <PageContainer maxWidth="wide" className="pb-28">
      <Spread>
        <PageHeader>{t('kpiReports.cashDashboard.pageHeader')}</PageHeader>
        <VStack gap="md" align="end">
          <HStack gap="sm">
            <PnlViewSelector value={view} onChange={handleViewChange} />
            <FilterControls
              filters={filters as Filter<unknown>[]}
              activeFilterCount={appliedFilterBadges.length}
              isOpen={isFiltersPanelOpen}
              onOpenChange={handleFiltersPanelOpenChange}
              onShowResults={showResults}
              onResetAllFilters={resetAllFilters}
            />
            <ExportCsvMenu
              items={[
                <ExportCsvMenuItem
                  label={t('kpiReports.export.dashboard')}
                  description={t('kpiReports.export.dashboardDescription')}
                  getCsvExport={exportDashboard}
                  rowCount={isPending ? 0 : dashboardRows.length}
                />,
                <ExportCsvMenuItem
                  label={t('kpiReports.cashDashboard.export.arReport')}
                  description={t('kpiReports.cashDashboard.export.arReportDescription')}
                  getCsvExport={exportArReport}
                  // The report's row count is only known server-side; this only holds the export back
                  // until the page has figures to export.
                  rowCount={isPending || error || months.length === 0 ? 0 : 1}
                  canExceedRowCountLimit
                  disabled={!canExportArReport}
                />,
              ]}
            />
          </HStack>
        </VStack>
      </Spread>
      <CrtVersionComputingNote crtPeriod={crtPeriod} versionId={search.loadedVersionId} />

      <CashSummaryCard
        crtPeriod={crtPeriod}
        pnlFilters={appliedFilters}
        actuals={isNextYear ? undefined : actualsData?.breakdown}
        forecast={isActualsOnly ? undefined : forecastsData?.breakdown}
        isNextYear={isNextYear}
        isActualsOnly={isActualsOnly}
        isPending={isPending}
        error={error}
        currency={conversion}
        isPerMil={!!search.isPerMil}
        availability={breakdownAvailability}
        productCategories={search.productCategory}
        brands={search.brand}
      />

      <Card className="w-full">
        <CardContent className="pt-6">
          {/* Same card as the table so the bar reads on white, as on the detail tables. */}
          <VStack gap="lg" align="stretch">
            <AppliedFiltersBar
              appliedFilters={appliedFilterBadges}
              onBadgeClick={handleBadgeClick}
            />
            <ArDashboardTable
              sections={AR_TABLE_SECTIONS}
              actuals={isNextYear ? undefined : actualsData?.breakdown}
              forecast={isActualsOnly ? undefined : forecastsData?.breakdown}
              months={months}
              crtPeriod={crtPeriod}
              isNextYear={isNextYear}
              isActualsOnly={isActualsOnly}
              isPending={isPending}
              error={error}
              currency={conversion}
              isPerMil={!!search.isPerMil}
              actions={<PerMilSwitch isPerMil={!!search.isPerMil} onChange={handlePerMilChange} />}
            />
          </VStack>
        </CardContent>
      </Card>

      <CrtVersionsFooter
        crtPeriod={crtPeriod}
        pricingAssumptionIds={search.pricingAssumptionIds}
        taxAssumptionIds={search.taxAssumptionIds}
        manualInvestmentIds={search.manualInvestmentIds}
        volumeScenarioId={search.volumeScenarioId}
        loadedVersionId={search.loadedVersionId}
        onLoadVersion={loadVersion}
        onSaved={markSaved}
      />
    </PageContainer>
  )
}
